import { usePaymentManager } from "@shared/modules/payments/contexts/PaymentManagerContext";
import Button from "@ui-kit/form/Button/Button.web";
import TickCircle from "@ui-kit/icons/TickCircle/TickCircle.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useState } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import { InvoiceFailedSharedProps } from "./InvoiceFailed.props";

export default function InvoiceFailed({
  basket,
}: InvoiceFailedProps): JSX.Element {
  const { t } = useTranslation("checkout");
  const { manager, handleSetPurchaseData } = usePaymentManager();
  const [retrievingInvoice, setRetrievingInvoice] = useState(false);

  const handleGetInvoice = async () => {
    if (!basket) {
      return;
    }

    setRetrievingInvoice(true);
    try {
      const invoice = await manager.getInvoice(basket);
      handleSetPurchaseData({
        finalBasket: basket,
        invoice,
      });
    } finally {
      setRetrievingInvoice(false);
    }
  };

  return (
    <Flex
      direction="column"
      align="center"
      gap="xl"
      data-testid="fail-invoice-retrieval"
    >
      <TickCircle.Solid
        h="element.4xl"
        w="element.4xl"
        colorPalette="primary"
      />
      <Box align="center">
        <Text fontSize="4xl" fontWeight={600} color="primary.800" mb="xs">
          {t("Order confirmed")}
        </Text>
        {basket ? (
          <Text mb="md">
            {t(
              "Your order was successful, however there was an error retrieving the details of your order. You can attempt to retrieve your order details again or you can check your email for your tickets."
            )}
          </Text>
        ) : (
          <Text mb="md">
            {t(
              "Your order was successful, however there was an error retrieving the details of your order. Please check your email for your tickets."
            )}
          </Text>
        )}
        <Button.Solid
          data-testid="order-retrieve-invoice"
          onClick={handleGetInvoice}
          isLoading={retrievingInvoice}
        >
          {t("Retry")}
        </Button.Solid>
      </Box>
    </Flex>
  );
}

export type InvoiceFailedProps = InvoiceFailedSharedProps;
